import React, { useState } from 'react';
import { invoicesService } from '../services';
import { formatCurrency, formatDate } from '../utils/formatters';
import RecordPaymentModal from './RecordPaymentModal';
import InvoicePreview from './InvoicePreview';
import useConfirm from './ui/useConfirm';

const InvoiceDetail = ({ invoice, onStatusChange, onEdit, getStatusBadgeColor }) => {
  const [confirm, confirmDialog] = useConfirm();
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleIssue = async () => {
    if (!(await confirm('It will be sent to the customer.', { title: 'Issue this invoice?', confirmText: 'Issue' }))) return;

    setLoading(true);
    setError('');
    try {
      await invoicesService.issue(invoice.id);
      setSuccess('Invoice issued successfully');
      onStatusChange();
    } catch (err) {
      setError(err.message || 'Failed to issue invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirm('This cannot be undone.', { title: 'Cancel this invoice?', confirmText: 'Cancel invoice' }))) return;

    setLoading(true);
    setError('');
    try {
      await invoicesService.cancel(invoice.id);
      setSuccess('Invoice cancelled');
      onStatusChange();
    } catch (err) {
      setError(err.message || 'Failed to cancel invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!(await confirm(`This records a payment of ${formatCurrency(invoice.balance)}.`, { title: 'Mark as fully paid?', confirmText: 'Mark paid' }))) return;

    setLoading(true);
    setError('');
    try {
      await invoicesService.markAsPaid(invoice.id);
      setSuccess('Invoice marked as paid');
      onStatusChange();
    } catch (err) {
      setError(err.message || 'Failed to mark as paid');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentRecorded = () => {
    setShowPaymentModal(false);
    setSuccess('Payment recorded successfully');
    onStatusChange();
  };

  const canEdit = invoice.status === 'draft';
  const canIssue = invoice.status === 'draft';
  const canCancel = invoice.status !== 'paid' && invoice.status !== 'cancelled';
  const canRecordPayment = ['issued', 'partial', 'overdue'].includes(invoice.status);
  const canMarkPaid = ['issued', 'partial', 'overdue'].includes(invoice.status) && invoice.balance > 0;

  if (showPreview) {
    return (
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <InvoicePreview
          invoice={invoice}
          onBack={() => setShowPreview(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 md:space-y-6">
      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-lg">{error}</div>
      )}
      {success && (
        <div className="p-4 bg-green-100 text-green-700 rounded-lg">{success}</div>
      )}

      {/* Actions Bar */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap pb-4 border-b">
        <button
          onClick={() => setShowPreview(true)}
          className="h-11 sm:h-10 px-3 font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
        >
          Print / Preview
        </button>
        {canEdit && (
          <button
            onClick={onEdit}
            className="h-11 sm:h-10 px-3 font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 text-sm"
            disabled={loading}
          >
            Edit
          </button>
        )}
        {canIssue && (
          <button
            onClick={handleIssue}
            className="h-11 sm:h-10 px-3 font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            disabled={loading}
          >
            Issue Invoice
          </button>
        )}
        {canRecordPayment && (
          <button
            onClick={() => setShowPaymentModal(true)}
            className="h-11 sm:h-10 px-3 font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            disabled={loading}
          >
            Record Payment
          </button>
        )}
        {canMarkPaid && (
          <button
            onClick={handleMarkPaid}
            className="h-11 sm:h-10 px-3 font-medium bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
            disabled={loading}
          >
            Mark as Paid
          </button>
        )}
        {canCancel && (
          <button
            onClick={handleCancel}
            className="h-11 sm:h-10 px-3 font-medium bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
            disabled={loading}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Invoice Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">Bill To</h3>
          <div className="text-lg font-semibold text-gray-900">{invoice.customerName}</div>
          <div className="text-gray-600">{invoice.customerPhone}</div>
          {invoice.customerLocation && (
            <div className="text-gray-600">{invoice.customerLocation}</div>
          )}
        </div>
        <div className="md:text-right">
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(invoice.status)}`}>
            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
          </span>
          <div className="mt-2 text-sm text-gray-600">
            <div><span className="font-medium">Issue Date:</span> {invoice.issueDate ? formatDate(invoice.issueDate) : 'Not issued'}</div>
            <div className={invoice.status === 'overdue' ? 'text-red-600 font-medium' : ''}>
              <span className="font-medium">Due Date:</span> {invoice.dueDate ? formatDate(invoice.dueDate) : 'Not set'}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-gray-50 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoice.lineItems?.map((item, index) => (
              <tr key={index}>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{item.productName}</div>
                  {item.description && (
                    <div className="text-xs text-gray-500">{item.description}</div>
                  )}
                  <div className="sm:hidden text-xs text-gray-500 tabular-nums">{item.quantity} × {formatCurrency(item.unitPrice)}</div>
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-right text-sm text-gray-900">{item.quantity}</td>
                <td className="hidden sm:table-cell px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-full md:w-1/3 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
          </div>
          {invoice.taxRate > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax ({invoice.taxRate}%):</span>
              <span className="font-medium">{formatCurrency(invoice.taxAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg border-t pt-2">
            <span className="font-medium">Total:</span>
            <span className="font-bold">{formatCurrency(invoice.totalAmount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Amount Paid:</span>
            <span className="font-medium text-green-600">{formatCurrency(invoice.amountPaid)}</span>
          </div>
          <div className="flex justify-between text-lg border-t pt-2">
            <span className="font-medium">Balance Due:</span>
            <span className={`font-bold ${invoice.balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {formatCurrency(invoice.balance)}
            </span>
          </div>
        </div>
      </div>

      {/* Payment History */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Payment History</h3>
          <div className="bg-gray-50 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <span className="capitalize">
                        {payment.paymentMethod?.replace('_', ' ')}
                        {payment.mobileMoneyProvider && ` (${payment.mobileMoneyProvider})`}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-sm text-gray-500">
                      {payment.transactionReference || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                      {formatCurrency(payment.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notes & Terms */}
      {(invoice.notes || invoice.terms) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
          {invoice.notes && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Notes</h4>
              <p className="text-sm text-gray-700">{invoice.notes}</p>
            </div>
          )}
          {invoice.terms && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Terms & Conditions</h4>
              <p className="text-sm text-gray-700">{invoice.terms}</p>
            </div>
          )}
        </div>
      )}

      {/* Record Payment Modal */}
      <RecordPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        invoice={invoice}
        onPaymentRecorded={handlePaymentRecorded}
      />
      {confirmDialog}
    </div>
  );
};

export default InvoiceDetail;
